import React, { useEffect, useState } from 'react';
import '../../styles/shared.css';
import '../../styles/powerups.css';
import { usePowerUps } from '../../../../hooks/usePowerUps';
import CustomDomainModal from './CustomDomainModal';

interface PowerUpsTabProps {
    handleForm: any;
}

const PowerUpsTab: React.FC<PowerUpsTabProps> = ({ handleForm }) => {
    const { togglePowerUp, isPowerUpEnabled } = usePowerUps(handleForm);
    const [showCustomDomainModal, setShowCustomDomainModal] = useState(false);

    const powerUpsList = [
        {
            name: 'smart-refresh',
            title: 'Smart Refresh',
            icon: '🔄',
            description: 'Sistema inteligente de refresh de anúncios baseado na atividade do usuário.',
            benefits: ['Aumento de receita', 'Melhor UX', 'Refresh otimizado'],
            available: true,
            alwaysEnabled: true,
        },
        {
            name: 'layout-shift-protection',
            title: 'Layout Shift Protection',
            icon: '📐',
            description: 'Protege contra mudanças de layout causadas pelo carregamento de anúncios.',
            benefits: ['Melhor Core Web Vitals', 'UX superior', 'SEO otimizado'],
            available: true,
            alwaysEnabled: true,
        },
        {
            name: 'bot-detection',
            title: 'Bot Detection',
            icon: '🤖',
            description: 'Detecta bots usando padrões de user agent, detecção de headless browser, checagens básicas de ambiente e gatilhos comportamentais simples.',
            benefits: [
                'Proteção básica contra bots',
                'Execução rápida',
                'Redução de tráfego inválido',
                'Checagens de ambiente e comportamento'
            ],
            available: true,
        },
        {
            name: 'advanced-bot-detection',
            title: 'Advanced Bot Detection',
            icon: '🛡️',
            description: 'Detecção avançada de bots com fingerprinting, machine learning, análise comportamental em tempo real, análise de rede e honeypot traps.',
            benefits: [
                'Fingerprinting avançado',
                'Machine learning para score de bots',
                'Análise comportamental em tempo real',
                'Validação de headers e honeypot traps',
                'Proteção máxima contra fraudes'
            ],
            available: true,
        },
        {
            name: 'prebid',
            title: 'Prebid.js',
            icon: '💰',
            description: 'Habilite o header bidding com Prebid.js para maximizar a competição entre SSPs e aumentar sua receita.',
            benefits: [
                'Aumento de receita',
                'Maior competição entre SSPs',
                'Integração flexível com diversos parceiros'
            ],
            available: true,
        },
        {
            name: 'prebid-user-sync',
            title: 'Prebid.js User Sync',
            icon: '🔗',
            description: 'Ative o User Sync do Prebid.js para melhorar a correspondência de usuários entre SSPs e aumentar o fill rate.',
            benefits: [
                'Melhor match de usuários',
                'Aumento do fill rate',
                'Sincronização eficiente de cookies'
            ],
            available: true,
        },
        {
            name: 'custom-tag-domain',
            title: 'First-Party Data & Custom Domain',
            icon: '🌐',
            description: 'Potencialize sua receita e privacidade com domínio próprio para a tag, garantindo coleta de dados first-party, mais controle e maior compatibilidade.',
            benefits: [
                'Coleta de dados first-party',
                'Aumento de receita',
                'Mais controle sobre privacidade e dados',
                'Melhor compatibilidade com bloqueadores',
                'Fortalece a relação publisher-anunciante',
            ],
            available: true,
        },
        {
            name: 'disable-gam-tag',
            title: 'Desativar GAM Tag',
            icon: '🚫',
            description: 'Desative a tag do Google Ad Manager para rodar anúncios diretamente via Header Bidding, reduzindo custos do Ad Manager 360 e otimizando a entrega dos anúncios.',
            benefits: [
                'Redução de custos do Ad Manager 360',
                'Entrega direta dos anúncios via Header Bidding',
                'Menos dependência do ad server do Google',
                'Otimização da performance e receita'
            ],
            available: true,
        },
        {
            name: 'realtime-monitoring',
            title: 'Real-Time Monitoring',
            icon: '⏱️',
            description: 'Monitoramento da tag em tempo real, incluindo pageviews, renderizações de anúncio, impressões e muito mais.',
            benefits: [
                'Acompanhamento em tempo real',
                'Métricas detalhadas de pageviews',
                'Monitoramento de impressões e renderizações',
                'Insights instantâneos sobre performance'
            ],
            available: false,
        },
        {
            name: 'ppid',
            title: 'Publisher Provided Identifier (PPID)',
            icon: '🆔',
            description: 'Habilite o PPID para incrementar sua receita com limited signals, melhorando a segmentação mesmo com restrições de privacidade.',
            benefits: [
                'Incremento de receita',
                'Melhor segmentação',
                'Funciona com sinais limitados (limited signals)'
            ],
            available: true,
        },
        {
            name: 'pps',
            title: 'Publisher Provided Signals (PPS)',
            icon: '📡',
            description: 'Envie sinais personalizados do publisher para os SSPs, melhorando a segmentação e o desempenho das campanhas mesmo em ambientes com restrições de dados.',
            benefits: [
                'Melhor segmentação de audiência',
                'Aumento do valor do inventário',
                'Funciona em ambientes com restrições de privacidade',
                'Permite sinais customizados para SSPs',
                'Potencializa campanhas contextuais'
            ],
            available: false,
        },
        {
            name: 'email-tracking',
            title: 'Email Intelligence & Audience Boost',
            icon: '✉️',
            description: 'Potencialize sua audiência e campanhas capturando e ativando emails de usuários logados, assinantes e newsletter, ampliando o alcance e a performance publicitária com dados valiosos e segmentação avançada.',
            benefits: [
                'Aumenta o valor do inventário com dados first-party',
                'Melhora a segmentação e o desempenho das campanhas',
                'Permite estratégias de remarketing e lookalike',
                'Expande o alcance de campanhas para usuários autenticados',
                'Conformidade LGPD/GDPR',
            ],
            available: true,
        },
        {
            name: 'consent-management',
            title: 'Consent Management',
            icon: '🔒',
            description: 'Gerencia o consentimento do usuário para LGPD/GDPR de forma automática e otimizada.',
            benefits: ['Conformidade LGPD/GDPR', 'UX otimizada', 'Maior taxa de consentimento'],
            available: false,
        },
        {
            name: 'adblock-detection',
            title: 'AdBlock Detection',
            icon: '🛡️',
            description: 'Detecta quando o usuário está usando AdBlock e pode exibir mensagens personalizadas ou conteúdo alternativo.',
            benefits: ['Recuperação de receita', 'Conscientização do usuário', 'Métricas de AdBlock'],
            available: true,
        },
        {
            name: 'network-latency-monitor',
            title: 'Monitoramento de Latência',
            icon: '🌐',
            description: 'Monitore e reporte a latência das chamadas de ad server e header bidding.',
            benefits: [
                'Identificação de gargalos',
                'Otimização de performance',
                'Melhor UX'
            ],
            available: false,
        },
    ];

    // Initialize all power ups in form state if not present
    useEffect(() => {
        const currentPowerUps = handleForm.values.powerUps || [];
        const allPowerUpNames = powerUpsList.map(p => p.name);
        
        // Check if we need to initialize any missing power ups
        const missingPowerUps = allPowerUpNames.filter(name => 
            !currentPowerUps.some((p: any) => p.name === name)
        );

        if (missingPowerUps.length > 0) {
            const updatedPowerUps = [...currentPowerUps];
            
            missingPowerUps.forEach(powerUpName => {
                const powerUpConfig = powerUpsList.find(p => p.name === powerUpName);
                let enabled = false;

                // Set initial enabled state based on power up type
                if (powerUpConfig?.alwaysEnabled) {
                    enabled = true;
                } else if (powerUpName === 'prebid') {
                    enabled = handleForm.values.prebid?.enabled || false;
                } else if (powerUpName === 'prebid-user-sync') {
                    enabled = handleForm.values.prebid?.userSyncEnabled || false;
                } else if (powerUpName === 'bot-detection') {
                    enabled = handleForm.values.botDetectionEnabled || false;
                } else if (powerUpName === 'advanced-bot-detection') {
                    enabled = handleForm.values.botDetectionAdvancedEnabled || false;
                } else if (powerUpName === 'disable-gam-tag') {
                    enabled = handleForm.values.disableGptAdServer || false;
                } else if (powerUpName === 'custom-tag-domain') {
                    enabled = !!(handleForm.values.customDomain);
                } else if (powerUpName === 'ppid') {
                    enabled = handleForm.values.ppidEnabled || false;
                } else if (powerUpName === 'email-tracking') {
                    enabled = handleForm.values.emailTrackingEnabled || false;
                } else if (powerUpName === 'adblock-detection') {
                    enabled = handleForm.valuesadBlockEnabled || false;
                }

                updatedPowerUps.push({ name: powerUpName, enabled });
            });

            handleForm.setFieldValue('powerUps', updatedPowerUps);
        }
    }, [handleForm.values.powerUps, powerUpsList]);

    const handlePowerUpClick = (powerUpName: string, available: boolean, alwaysEnabled: boolean = false) => {
        if (available && !alwaysEnabled) {
            togglePowerUp(powerUpName);
        }
    };

    const handleToggleClick = (e: React.MouseEvent, powerUpName: string, available: boolean, alwaysEnabled: boolean = false) => {
        e.stopPropagation(); // Prevent card click event
        if (available && !alwaysEnabled) {
            togglePowerUp(powerUpName);
        }
    };

    const handleConfigClick = (e: React.MouseEvent, powerUpName: string) => {
        e.stopPropagation(); // Prevent card click event
        if (powerUpName === 'custom-tag-domain') {
            setShowCustomDomainModal(true);
        }
    };

    // Calculate statistics
    const availablePowerUps = powerUpsList.filter(p => p.available);
    const activePowerUps = availablePowerUps.filter(p => 
        p.alwaysEnabled || isPowerUpEnabled(p.name)
    );
    const inactivePowerUps = availablePowerUps.filter(p => 
        !p.alwaysEnabled && !isPowerUpEnabled(p.name)
    );

    return (
        <div className="content-section">
            {/* Header com estatísticas */}
            <div className="d-flex justify-content-between align-items-end mb-4 pb-3 border-bottom">
                <div className="flex-grow-1">
                    <h3 className="section-title mb-2">⚡ Power-Ups</h3>
                    <p className="text-muted mb-0 small">
                        Habilite funcionalidades avançadas para otimizar sua experiência publicitária.
                        Alguns recursos estão em desenvolvimento e estarão disponíveis em breve.
                    </p>
                </div>
                <div className="d-flex gap-4 ms-4">
                    <div className="text-center">
                        <div className="fs-3 fw-bold text-primary lh-1">{availablePowerUps.length}</div>
                        <small className="text-muted text-uppercase fw-medium" style={{fontSize: '11px', letterSpacing: '0.5px'}}>Disponíveis</small>
                    </div>
                    <div className="text-center">
                        <div className="fs-3 fw-bold text-success lh-1">{activePowerUps.length}</div>
                        <small className="text-muted text-uppercase fw-medium" style={{fontSize: '11px', letterSpacing: '0.5px'}}>Ativos</small>
                    </div>
                </div>
            </div>

            <div className="powerups-grid">
                {powerUpsList.map((powerUp) => {
                    const isEnabled = powerUp.alwaysEnabled || isPowerUpEnabled(powerUp.name);

                    return (
                        <div
                            key={powerUp.name}
                            className={`powerup-card ${isEnabled && powerUp.available ? 'enabled' : 'disabled'} ${!powerUp.available ? 'coming-soon' : ''} ${powerUp.alwaysEnabled ? 'always-enabled' : ''}`}
                            onClick={() => handlePowerUpClick(powerUp.name, powerUp.available, powerUp.alwaysEnabled)}
                        >
                            <div className="d-flex justify-content-between align-items-start mb-3">
                                <div className="d-flex align-items-start gap-3 flex-grow-1">
                                    <div className="powerup-icon">{powerUp.icon}</div>
                                    <div className="flex-grow-1">
                                        <h4 className="powerup-name">{powerUp.title}</h4>
                                        <p className="powerup-description">{powerUp.description}</p>
                                    </div>
                                </div>

                                <div className="d-flex flex-column align-items-center gap-2 flex-shrink-0">
                                    {powerUp.available ? (
                                        <>
                                            <div className="d-flex align-items-center gap-2">
                                                {powerUp.name === 'custom-tag-domain' && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-sm powerup-config-btn"
                                                        onClick={(e) => handleConfigClick(e, powerUp.name)}
                                                        title="Configurar domínio customizado"
                                                    >
                                                        ⚙️
                                                    </button>
                                                )}
                                                <label
                                                    className="toggle-switch"
                                                    onClick={(e) => powerUp.alwaysEnabled ? '' : handleToggleClick(e, powerUp.name, powerUp.available, powerUp.alwaysEnabled)}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isEnabled}
                                                        onChange={() => { }} // Controlled by the label onClick
                                                        onClick={(e) => e.stopPropagation()} // Prevent double handling
                                                    />
                                                    <span className="toggle-slider"></span>
                                                </label>
                                            </div>
                                            <span className="toggle-label">
                                                {isEnabled ? 'Habilitado' : 'Desabilitado'}
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <div className="coming-soon-badge">
                                                <span className="badge-text">Em Breve</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="mt-3 pt-3 border-top">
                                <div className="mb-2">
                                    <strong className="text-muted small">💡 Benefícios:</strong>
                                </div>
                                <ul className="list-unstyled mb-0">
                                    {powerUp.benefits.map((benefit, idx) => (
                                        <li key={idx} className="d-flex align-items-center gap-2 py-1 small">
                                            <span className="text-success fw-bold">✓</span>
                                            {benefit}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Custom Domain Configuration Modal */}
            <CustomDomainModal
                isOpen={showCustomDomainModal}
                toggle={() => setShowCustomDomainModal(false)}
                handleForm={handleForm}
            />
        </div>
    );
};

export default PowerUpsTab;